/**
 * IncompatibilityBanner - Reusable warning banner for admin tools
 * Displays when a tool needs updates to support new features
 */

import React, { useState } from 'react';
import { AlertTriangle, AlertCircle, ChevronDown, ChevronRight, X } from 'lucide-react';

const IncompatibilityBanner = ({
  title,
  issues = [],
  status,
  severity = 'warning', // 'warning' | 'error' | 'info'
  onDismiss = null,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  // Severity-based styling
  const severityStyles = {
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'text-red-600',
      text: 'text-red-900',
      subtext: 'text-red-700',
      badge: 'bg-red-100 text-red-800'
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'text-amber-600',
      text: 'text-amber-900',
      subtext: 'text-amber-700',
      badge: 'bg-amber-100 text-amber-800'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'text-blue-600',
      text: 'text-blue-900',
      subtext: 'text-blue-700',
      badge: 'bg-blue-100 text-blue-800'
    }
  };

  const styles = severityStyles[severity] || severityStyles.warning;
  const Icon = severity === 'error' ? AlertCircle : AlertTriangle;

  return (
    <div className={`${styles.bg} ${styles.border} border-2 rounded-lg p-4 mb-6 ${className}`}>
      {/* Header */}
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${styles.icon} flex-shrink-0 mt-0.5`} />

        <div className="flex-1 min-w-0">
          {/* Title Row */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-semibold text-sm ${styles.text}`}>
                {title}
              </h3>
              {status && (
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles.badge}`}>
                  {status}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Expand/Collapse Button */}
              {issues.length > 0 && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className={`p-1 rounded hover:bg-black/5 transition-colors ${styles.icon}`}
                  title={isExpanded ? 'Collapse details' : 'Expand details'}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              )}

              {/* Dismiss Button */}
              {onDismiss && (
                <button
                  onClick={handleDismiss}
                  className={`p-1 rounded hover:bg-black/5 transition-colors ${styles.icon}`}
                  title="Dismiss warning"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Collapsed View - Show issue count */}
          {!isExpanded && issues.length > 0 && (
            <p className={`text-xs ${styles.subtext}`}>
              {issues.length} update{issues.length !== 1 ? 's' : ''} required. Click to see details.
            </p>
          )}

          {/* Expanded View - Show all issues */}
          {isExpanded && issues.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className={`text-xs font-medium ${styles.text} mb-2`}>
                Updates Needed:
              </p>
              <ul className={`space-y-1.5 text-xs ${styles.subtext}`}>
                {issues.map((issue, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="flex-shrink-0 mt-0.5">•</span>
                    <span className="flex-1">{issue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncompatibilityBanner;
